<template>
  <section
    class="bg-blue relative z-2 -mt-8 overflow-hidden rounded-t-[40px] py-20 md:py-28"
  >
    <DefaultGrid>
      <div
        class="col-span-full grid justify-items-center gap-10 lg:grid-cols-2 lg:items-center lg:justify-items-stretch lg:gap-x-16 lg:gap-y-8"
      >
        <!-- Title: centered on top for mobile/tablet, right column on desktop -->
        <h2
          class="type-font-salt-ss02 text-center text-[56px] leading-[0.9] font-medium tracking-[-0.03em] md:mb-8 md:text-[80px] lg:col-start-2 lg:row-start-1 lg:text-left"
        >
          Supporters
        </h2>

        <!-- Photo with decorative burst -->
        <div
          class="relative mb-10 flex w-full justify-center md:mb-0 lg:col-start-1 lg:row-span-2 lg:row-start-1"
        >
          <img
            src="/images/background-star.svg"
            alt=""
            class="pointer-events-none absolute top-1/2 w-[95%] max-w-none -translate-y-1/2 opacity-20 lg:w-full"
          />
          <div
            class="relative w-[280px] max-w-full -rotate-5 overflow-hidden rounded-[20px]"
          >
            <div class="aspect-square">
              <PayloadImage
                v-if="current"
                class="h-full w-full object-cover object-center"
                :image="current.image"
                :sizes="{ default: '280px' }"
                lazy
              />
            </div>
          </div>
        </div>

        <!-- Quote block -->
        <div class="flex w-full flex-col lg:col-start-2 lg:row-start-2">
          <!-- Quotes share one grid cell so the block always reserves the tallest -->
          <div class="mb-8 grid">
            <p
              v-for="(supporter, i) in supporters"
              :key="supporter.id"
              class="type-heading-4 col-start-1 row-start-1 transition-opacity duration-300"
              :class="
                i === index ? 'opacity-100' : 'pointer-events-none opacity-0'
              "
              :aria-hidden="i !== index"
            >
              “{{ supporter.quote }}”
            </p>
          </div>
          <div class="mb-8 grid">
            <div
              v-for="(supporter, i) in supporters"
              :key="supporter.id"
              class="col-start-1 row-start-1 transition-opacity duration-300"
              :class="
                i === index ? 'opacity-100' : 'pointer-events-none opacity-0'
              "
              :aria-hidden="i !== index"
            >
              <p class="font-medium" v-text="supporter.name" />
              <p class="opacity-50" v-text="supporter.role" />
            </div>
          </div>
          <div v-if="supporters.length > 1" class="flex gap-x-3">
            <button
              type="button"
              class="flex h-13 w-13 items-center justify-center rounded-full bg-white/50 transition-colors hover:bg-white"
              aria-label="Previous supporter"
              @click="step(-1)"
            >
              <SvgGlyphArrowRight class="h-5 w-5 -scale-x-100" />
            </button>
            <button
              type="button"
              class="flex h-13 w-13 items-center justify-center rounded-full bg-white/50 transition-colors hover:bg-white"
              aria-label="Next supporter"
              @click="step(1)"
            >
              <SvgGlyphArrowRight class="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </DefaultGrid>
  </section>
</template>

<script setup lang="ts">
import type { Supporter } from '#payload-types'

interface Props {
  supporters: Supporter[]
}

const props = defineProps<Props>()

const index = ref(0)
const current = computed(() => props.supporters[index.value] ?? null)

const step = (delta: number) => {
  const { length } = props.supporters

  index.value = (index.value + delta + length) % length
}
</script>
